import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { AutoLinkPlugin, createLinkMatcherWithRegExp } from '@lexical/react/LexicalAutoLinkPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { CodeHighlightNode, CodeNode, normalizeCodeLanguage, registerCodeHighlighting } from '@lexical/code';
import { $getRoot } from 'lexical';
import { $convertFromMarkdownString, $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import { detectCodeLanguage } from './codeLanguageDetection.js';
import styles from '../stylesheets/Sidebar.module.css';

const NOTES_LINK_MATCHERS = [
  createLinkMatcherWithRegExp(
    /((https?:\/\/|www\.)[^\s<]+)/i,
    (text) => (text.startsWith('http://') || text.startsWith('https://') ? text : `https://${text}`),
  ),
];

function NotesCodeHighlightPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return registerCodeHighlighting(editor);
  }, [editor]);

  return null;
}

function NotesCodeLanguageDetectionPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerNodeTransform(CodeNode, (codeNode) => {
      const detectedLanguage = detectCodeLanguage(codeNode.getTextContent());
      if (!detectedLanguage) return;

      const currentLanguage = codeNode.getLanguage();
      const normalizedCurrentLanguage = currentLanguage
        ? (normalizeCodeLanguage(currentLanguage) || currentLanguage)
        : null;

      const isAutoLanguage =
        normalizedCurrentLanguage === null
        || normalizedCurrentLanguage === 'js'
        || normalizedCurrentLanguage === 'javascript'
        || normalizedCurrentLanguage === 'plain'
        || normalizedCurrentLanguage === 'clike';

      if (!isAutoLanguage) return;
      if (normalizedCurrentLanguage === detectedLanguage) return;

      codeNode.setLanguage(detectedLanguage);
    });
  }, [editor]);

  return null;
}

function NotesPlaceholder() {
  return (
    <div className={styles.notesPlaceholder}>
      <div className={styles.notesPlaceholderBlock}>
        <p><strong>What&apos;s on your mind?</strong></p>
        <p>Click here to start writing</p>
      </div>

      <div className={styles.notesPlaceholderBlock}>
        <p><strong>✅ Markdown is supported</strong></p>
        <p>Format and outline thoughts quickly with markdown</p>
      </div>

      <div className={styles.notesPlaceholderBlock}>
        <p><strong>🔒 Everything stays 100% private</strong></p>
        <p>Notes and to-dos are saved to your device&apos;s storage, nothing ever gets sent to a server</p>
      </div>
    </div>
  );
}

export default function NotesEditor({
  initialMarkdown = '',
  onMarkdownChange,
  onBlur = () => {},
  onUserInput = () => {},
}) {
  const editorTheme = useMemo(
    () => ({
      paragraph: styles.notesParagraph,
      quote: styles.notesQuote,
      heading: {
        h1: styles.notesH1,
        h2: styles.notesH2,
        h3: styles.notesH3,
      },
      list: {
        ul: styles.notesUl,
        ol: styles.notesOl,
        listitem: styles.notesListItem,
        nested: {
          listitem: styles.notesNestedListItem,
        },
      },
      code: styles.notesCodeBlock,
      codeHighlight: {
        atrule: styles.notesCodeTokenAtrule,
        attr: styles.notesCodeTokenAttr,
        boolean: styles.notesCodeTokenBoolean,
        builtin: styles.notesCodeTokenBuiltin,
        cdata: styles.notesCodeTokenCdata,
        char: styles.notesCodeTokenChar,
        class: styles.notesCodeTokenClassName,
        'class-name': styles.notesCodeTokenClassName,
        comment: styles.notesCodeTokenComment,
        constant: styles.notesCodeTokenConstant,
        deleted: styles.notesCodeTokenDeleted,
        doctype: styles.notesCodeTokenDoctype,
        entity: styles.notesCodeTokenEntity,
        function: styles.notesCodeTokenFunction,
        important: styles.notesCodeTokenImportant,
        inserted: styles.notesCodeTokenInserted,
        keyword: styles.notesCodeTokenKeyword,
        namespace: styles.notesCodeTokenNamespace,
        number: styles.notesCodeTokenNumber,
        operator: styles.notesCodeTokenOperator,
        prolog: styles.notesCodeTokenProlog,
        property: styles.notesCodeTokenProperty,
        punctuation: styles.notesCodeTokenPunctuation,
        regex: styles.notesCodeTokenRegex,
        selector: styles.notesCodeTokenSelector,
        string: styles.notesCodeTokenString,
        symbol: styles.notesCodeTokenSymbol,
        tag: styles.notesCodeTokenTag,
        url: styles.notesCodeTokenUrl,
        variable: styles.notesCodeTokenVariable,
      },
      link: styles.notesLink,
      text: {
        bold: styles.notesTextBold,
        italic: styles.notesTextItalic,
        underline: styles.notesTextUnderline,
        code: styles.notesTextCode,
      },
    }),
    [],
  );

  const initialConfig = useMemo(
    () => ({
      namespace: 'FocusTodoNotesEditor',
      theme: editorTheme,
      onError: (error) => {
        throw error;
      },
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode, CodeNode, CodeHighlightNode],
      editorState: () => {
        const root = $getRoot();
        root.clear();

        const startingMarkdown =
          (initialMarkdown && initialMarkdown.trim().length > 0)
            ? initialMarkdown
            : '';

        if (startingMarkdown.length > 0) {
          $convertFromMarkdownString(startingMarkdown, TRANSFORMERS);
        }
      },
    }),
    [editorTheme, initialMarkdown],
  );

  const handleKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    const isCharacterInput = event.key.length === 1;
    const isEditingKey =
      event.key === 'Backspace'
      || event.key === 'Delete'
      || event.key === 'Enter'
      || event.key === 'Tab';

    if (isCharacterInput || isEditingKey) {
      onUserInput();
    }
  };

  const handleBlur = (event) => {
    const textContent = event.currentTarget.textContent || '';
    onBlur(textContent);
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={styles.notesEditorShell}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={styles.notesContentEditable}
              aria-label="Markdown notes editor"
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onPaste={onUserInput}
              onCut={onUserInput}
              onDrop={onUserInput}
              onCompositionEnd={onUserInput}
            />
          }
          placeholder={<NotesPlaceholder />}
          ErrorBoundary={LexicalErrorBoundary}
        />

        <HistoryPlugin />
        <ListPlugin />
        <TabIndentationPlugin maxIndent={7} />
        <LinkPlugin />
        <AutoLinkPlugin matchers={NOTES_LINK_MATCHERS} />
        <ClickableLinkPlugin newTab={true} />
        <NotesCodeHighlightPlugin />
        <NotesCodeLanguageDetectionPlugin />
        <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
        <OnChangePlugin
          ignoreSelectionChange={true}
          onChange={(editorState) => {
            editorState.read(() => {
              const markdown = $convertToMarkdownString(TRANSFORMERS);
              onMarkdownChange(markdown);
            });
          }}
        />
      </div>
    </LexicalComposer>
  );
}

NotesEditor.propTypes = {
  initialMarkdown: PropTypes.string,
  onMarkdownChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onUserInput: PropTypes.func,
};
